import { Component, Input, ViewChild, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LineItem } from "../models/lineitem";
import { Track, ITrack } from "../models/track";
import { EntryLocation } from '../models/entrylocation';
import { AuthorityPoint } from '../models/authoritypoint';
import { DataService } from '../services/data.service';
import { LineItemModel } from '../models/lineitemmodel';
import { AuthorityPointGroup } from '../models/authoritypointgroup';
import { Direction } from '../models/direction.enum';
import { PointType } from '../models/pointtype.enum';
import { Location } from '../models/location';
import { SelectItem } from 'primeng/api';

@Component({
    selector: 'line-item',
    templateUrl: './lineitem.component.html',
    styleUrls: ['./lineitem.component.scss']
})
export class LineItemComponent implements OnChanges {
    @Input() lineItemModel = new LineItemModel();
    @Input() authorityPointGroupList: AuthorityPointGroup[];
    @Output() block: EventEmitter<boolean> = new EventEmitter();
    @Output() outOfRangeViolation: EventEmitter<boolean> = new EventEmitter();

    @Input() chosenMilepost: string = '';
    @Input() chosenPrefix: string = '';

    displayWarning: boolean = false;
    displayPrefixWarning: boolean = false;

    private readonly directionsInOrder: Array<Direction> = [Direction.N, Direction.S, Direction.E, Direction.W, Direction.B];

    filteredOperatingFromList: AuthorityPointGroup[];
    filteredOperatingToList: AuthorityPointGroup[];
    trackListOperatingTo: SelectItem[] = new Array<SelectItem>();
    trackListOperatingFrom: SelectItem[] = new Array<SelectItem>();
    directionList: SelectItem[] = new Array<SelectItem>();

    constructor(private dataService: DataService, private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.lineItemModel && changes.lineItemModel != null) {
            if (this.lineItemModel.operatingFrom !== undefined && this.lineItemModel.operatingFrom !== null) {
                this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingFrom.points, this.trackListOperatingFrom);
            } else {
                this.trackListOperatingFrom = [{ label: 'Select Track', value: null }];
                this.lineItemModel.selectedTrackFrom = null;
            }
            if (this.lineItemModel.operatingTo !== undefined && this.lineItemModel.operatingTo !== null) {
                this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingTo.points, this.trackListOperatingTo);
            } else {
                this.trackListOperatingTo = [{ label: 'Select Track', value: null }];
                this.lineItemModel.selectedTrackTo = null;
            }
            this.loadDirectionDropdown(this.directionsInOrder);
        }
        if (changes.authorityPointGroupList && changes.authorityPointGroupList != null && !(this.lineItemModel === null || this.lineItemModel === undefined)) {
            this.handleAuthorityPointGroupListChange();
        }

        this.checkMilepostRanges();
        this.checkPrefixDifference();
    }

    private handleLineItemModelChange(): void {
        if (this.lineItemModel.operatingFrom !== undefined && this.lineItemModel.operatingFrom !== null) {
            this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingFrom.points, this.trackListOperatingFrom);
        } else {
            this.trackListOperatingFrom = [{ label: 'Select Track', value: null }];
            this.lineItemModel.selectedTrackFrom = null;
        }
        if (this.lineItemModel.operatingTo !== undefined && this.lineItemModel.operatingTo !== null) {
            this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingTo.points, this.trackListOperatingTo);
        } else {
            this.trackListOperatingTo = [{ label: 'Select Track', value: null }];
            this.lineItemModel.selectedTrackTo = null;
        }
        this.loadDirectionDropdown(this.directionsInOrder);
    }

    private handleAuthorityPointGroupListChange(): void {
        this.resetSelectedAuthorityPointGroups("From");
        this.resetSelectedAuthorityPointGroups("To");

        // Repopulate track lists after resetting to ensure dropdowns sync properly
        if (this.lineItemModel.operatingFrom && this.lineItemModel.operatingFrom.points) {
            this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingFrom.points, this.trackListOperatingFrom);
            // Match the selected track to the exact reference in the track list
            if (this.lineItemModel.selectedTrackFrom) {
                this.matchSelectedTrackToList('From');
            }
        }
        if (this.lineItemModel.operatingTo && this.lineItemModel.operatingTo.points) {
            this.populateTrackListFromAuthorityPointList(this.lineItemModel.operatingTo.points, this.trackListOperatingTo);
            // Match the selected track to the exact reference in the track list
            if (this.lineItemModel.selectedTrackTo) {
                this.matchSelectedTrackToList('To');
            }
        }

        // Update operating location display names when authority point groups are loaded
        if (this.lineItemModel.operatingFrom && this.lineItemModel.selectedTrackFrom) {
            this.updateOperatingLocationName('From');
        }
        if (this.lineItemModel.operatingTo && this.lineItemModel.selectedTrackTo) {
            this.updateOperatingLocationName('To');
        }
    }

    private matchSelectedTrackToList(selector: string): void {
        const trackSelector = 'selectedTrack' + selector;
        const trackListSelector = 'trackListOperating' + selector;

        if (!this.lineItemModel[trackSelector]) {
            return;
        }

        const currentTrack = this.lineItemModel[trackSelector];
        const matchingItem = this[trackListSelector].find((item: SelectItem) =>
            item.value && this.matchesTracks(item.value, currentTrack)
        );

        if (matchingItem) {
            this.lineItemModel[trackSelector] = matchingItem.value;
        }
    }

    search(event, isOperatingFrom: boolean): void {
        const query = event.query.toLowerCase();

        let filteredList = new Array<AuthorityPointGroup>();
        for (let i: number = 0; i < this.authorityPointGroupList.length; i++) {
            let authorityPoint: AuthorityPointGroup = this.authorityPointGroupList[i];
            if (query === ""
                || authorityPoint.commonName.toLowerCase().indexOf(query) == 0) {
                filteredList.push(authorityPoint);
            }
        }

        if (isOperatingFrom) {
            this.filteredOperatingFromList = filteredList;
        } else {
            this.filteredOperatingToList = filteredList;
        }
    }

    operatingFromSelected(): void {
        this.lineItemModel.selectedTrackFrom = null;
        if (typeof this.lineItemModel.operatingFrom === 'string' || this.lineItemModel.operatingFrom instanceof String) {
            const operatingFrom = this.lineItemModel.operatingFrom.toUpperCase();
            for (let operatingPoint of this.filteredOperatingFromList) {
                if (operatingPoint.commonName === operatingFrom) {
                    this.lineItemModel.operatingFrom = operatingPoint;
                    break;
                }
            }
        }
        this.lineItemModel.errorMessageOperatingFrom = this.handleBlur(this.lineItemModel.operatingFrom, this.trackListOperatingFrom, 'from');
        this.trackListOperatingFrom = [...this.trackListOperatingFrom]
        this.changeDetectorRef.detectChanges();
        if (this.lineItemModel.errorMessageOperatingFrom !== null) {
            this.trackListOperatingFrom = [{ label: 'Select Track', value: null }];
            this.lineItemModel.selectedTrackFrom = null;
        }
        this.checkMilepostRanges();
        this.checkPrefixDifference();
    }

    operatingToSelected(): void {
        this.lineItemModel.selectedTrackTo = null;
        if (typeof this.lineItemModel.operatingTo === 'string' || this.lineItemModel.operatingTo instanceof String) {
            const operatingTo = this.lineItemModel.operatingTo.toUpperCase();
            for (let operatingPoint of this.filteredOperatingToList) {
                if (operatingPoint.commonName === operatingTo) {
                    this.lineItemModel.operatingTo = operatingPoint;
                    break;
                }
            }
        }
        this.lineItemModel.errorMessageOperatingTo = this.handleBlur(this.lineItemModel.operatingTo, this.trackListOperatingTo, 'to');
        this.trackListOperatingTo = [...this.trackListOperatingTo]
        this.changeDetectorRef.detectChanges();
        if (this.lineItemModel.errorMessageOperatingTo !== null) {
            this.trackListOperatingTo = [{ label: 'Select Track', value: null }];
            this.lineItemModel.selectedTrackTo = null;
        }
        this.checkMilepostRanges();
        this.checkPrefixDifference();
    }



    private handleBlur(operatingLocation: AuthorityPointGroup, trackList: SelectItem[], dropdown: 'from' | 'to'): string {
        if (typeof operatingLocation === 'string' || operatingLocation instanceof String) {
            let operatingLocationStr: string = (operatingLocation + "").toUpperCase();
            let validationString: string = LineItemModel.validateStringAuthorityPoint(operatingLocationStr);
            if (validationString !== null) {
                return validationString;
            }

            this.loadDirectionDropdown(this.directionsInOrder);

            let arr: string[] = operatingLocationStr.split(" ");
            let prefix: string = arr[0];
            let milepost: string = arr[1];

            this.block.emit(true);
            this.dataService.getTracksByPrefixMilepost(prefix, milepost)
                .then(response => {
                    trackList.splice(0);
                    if (response !== undefined) {
                        for (let item of response) {
                            let authorityPoint: AuthorityPoint = {
                                type: PointType.MILEPOST,
                                displayName: prefix + " " + milepost,
                                location: {
                                    prefix: prefix, milepost: milepost, track: new Track(item)
                                } as Location
                            } as AuthorityPoint;
                            trackList.push({ label: authorityPoint.location.track.name, value: authorityPoint });
                        }
                    }
                    // Replace array reference to trigger dropdown refresh
                    if (dropdown === 'from') {
                        this.trackListOperatingFrom = [...trackList];
                    } else if (dropdown === 'to') {
                        this.trackListOperatingTo = [...trackList];
                    }
                    this.block.emit(false);
                })
                .catch(exc => {
                    this.block.emit(false);
                });
        } else if (operatingLocation !== undefined && operatingLocation !== null) {
            this.populateTrackListFromAuthorityPointList(operatingLocation.points, trackList);
            // Replace array reference to trigger dropdown refresh
            if (trackList === this.trackListOperatingFrom) {
                this.trackListOperatingFrom = [...trackList];
            } else if (trackList === this.trackListOperatingTo) {
                this.trackListOperatingTo = [...trackList];
            }
        }
        return null;
    }

    private populateTrackListFromAuthorityPointList(authorityPointList: AuthorityPoint[], trackList: SelectItem[]) {

        trackList.splice(0);

        if (authorityPointList === undefined || authorityPointList === null) {
            return;
        }

        let trackCountMap: Map<string, number> = new Map<string, number>();

        authorityPointList.forEach((authorityPoint: AuthorityPoint) => {
            let track: string = authorityPoint.location.track.name;
            if (trackCountMap.get(track) === undefined) {
                trackCountMap.set(track, 0);
            }
            trackCountMap.set(track, trackCountMap.get(track) + 1);
        });

        authorityPointList.forEach((authorityPoint: AuthorityPoint) => {
            let label: string = authorityPoint.location.track.name;
            if (trackCountMap.get(label) > 1) {
                label = label + "(" + authorityPoint.location.prefix + " " + Math.round(parseFloat(authorityPoint.location.milepost) * 100) / 100 + ")";
            }
            trackList.push({ label: label, value: authorityPoint });
        });
    }

    loadDirectionDropdown(directions: Array<Direction>): void {
        this.directionList.splice(0);
        if (directions === null) {
            if (this.lineItemModel.selectedTrackFrom === null || this.lineItemModel.selectedTrackTo === null) {
                return;
            }
            directions = new Array<Direction>();
            if (this.lineItemModel.selectedTrackFrom.location.subdivision === undefined || this.lineItemModel.selectedTrackFrom.location.subdivision.directions === undefined
                || this.lineItemModel.selectedTrackTo.location.subdivision === undefined || this.lineItemModel.selectedTrackTo.location.subdivision.directions === undefined) {
                directions = this.directionsInOrder;
            } else {
                this.lineItemModel.selectedTrackFrom.location.subdivision
                    .directions.forEach(direction => directions.push(Direction[direction.toString()]));
                this.lineItemModel.selectedTrackTo.location.subdivision
                    .directions.forEach(direction => directions.push(Direction[direction.toString()]));
            }
        }

        this.directionsInOrder.forEach(directionInOrder => {
            if (directions.findIndex(direction => direction === directionInOrder) !== -1) {
                this.directionList.push({ label: directionInOrder.toString(), value: Direction[directionInOrder].toString() })
            }
        });
    }

    private resetSelectedAuthorityPointGroups(selector: string): void {
        let authorityPointSelector: string = "operating" + selector;
        let trackSelector: string = "selectedTrack" + selector;
        let trackListSelector: string = "trackListOperating" + selector;
        if (this.lineItemModel[authorityPointSelector] !== undefined && this.lineItemModel[authorityPointSelector] !== null) {
            const currentCommonName = this.lineItemModel[authorityPointSelector].commonName;
            const baseName = this.getBaseName(currentCommonName);

            let authPoint: AuthorityPointGroup = this.authorityPointGroupList.find((authorityPoint: AuthorityPointGroup) => {
                const authBaseName = this.getBaseName(authorityPoint.commonName);
                return this.matchesCommonName(authorityPoint.commonName, currentCommonName, baseName) &&
                    authorityPoint.controlPointNumber === this.lineItemModel[authorityPointSelector].controlPointNumber &&
                    authorityPoint.type === this.lineItemModel[authorityPointSelector].type;
            });

            if (authPoint === undefined) {
                this.lineItemModel[authorityPointSelector] = null;
                this.lineItemModel[trackSelector] = null;
                this[trackListSelector].splice(0);
                this.loadDirectionDropdown(null);
                this.lineItemModel.selectedDirection = null;
            } else {
                this.lineItemModel[authorityPointSelector] = authPoint;

                if (this.lineItemModel[trackSelector] && authPoint.points) {
                    const oldSelectedTrack = this.lineItemModel[trackSelector];
                    const newSelectedTrack = authPoint.points.find((point: AuthorityPoint) =>
                        this.matchesTracks(point, oldSelectedTrack)
                    );
                    if (newSelectedTrack) {
                        this.lineItemModel[trackSelector] = newSelectedTrack;
                    }
                }
            }
        }
    }

    onTrackFromChange(event: any) {
        if (typeof this.lineItemModel.operatingFrom !== 'string' && this.lineItemModel.operatingFrom.commonName
            && this.lineItemModel.operatingFrom && this.lineItemModel.selectedTrackFrom) {
            const baseName = this.getBaseName(this.lineItemModel.operatingFrom.commonName);
            const updatedCommonName = this.buildFormattedName(baseName, this.lineItemModel.selectedTrackFrom);

            let idx = this.filteredOperatingFromList ? this.filteredOperatingFromList.findIndex(
                item => this.matchesCommonName(item.commonName, this.lineItemModel.operatingFrom.commonName, baseName)
            ) : -1;

            if (idx !== -1 && this.filteredOperatingFromList) {
                const updatedObj: AuthorityPointGroup = {
                    ...this.filteredOperatingFromList[idx],
                    commonName: updatedCommonName
                };
                this.filteredOperatingFromList[idx] = updatedObj;
                this.lineItemModel.operatingFrom = updatedObj;
            } else {
                const apgIdx = this.authorityPointGroupList.findIndex(
                    item => this.matchesCommonName(item.commonName, this.lineItemModel.operatingFrom.commonName, baseName)
                );
                if (apgIdx !== -1) {
                    const updatedObj: AuthorityPointGroup = {
                        ...this.authorityPointGroupList[apgIdx],
                        commonName: updatedCommonName
                    };
                    this.lineItemModel.operatingFrom = updatedObj;
                }
            }
        }
        this.checkMilepostRanges();
        this.checkPrefixDifference();
    }

    onTrackToChange(event: any) {
        if (typeof this.lineItemModel.operatingTo !== 'string' && this.lineItemModel.operatingTo && this.lineItemModel.selectedTrackTo) {
            const baseName = this.getBaseName(this.lineItemModel.operatingTo.commonName);
            const updatedCommonName = this.buildFormattedName(baseName, this.lineItemModel.selectedTrackTo);

            // Try to find in filtered list first
            let idx = this.filteredOperatingToList ? this.filteredOperatingToList.findIndex(
                item => this.matchesCommonName(item.commonName, this.lineItemModel.operatingTo.commonName, baseName)
            ) : -1;

            if (idx !== -1 && this.filteredOperatingToList) {
                const updatedObj: AuthorityPointGroup = {
                    ...this.filteredOperatingToList[idx],
                    commonName: updatedCommonName
                };
                this.filteredOperatingToList[idx] = updatedObj;
                this.lineItemModel.operatingTo = updatedObj;
            } else {
                // Fall back to authority point group list
                const apgIdx = this.authorityPointGroupList.findIndex(
                    item => this.matchesCommonName(item.commonName, this.lineItemModel.operatingTo.commonName, baseName)
                );
                if (apgIdx !== -1) {
                    const updatedObj: AuthorityPointGroup = {
                        ...this.authorityPointGroupList[apgIdx],
                        commonName: updatedCommonName
                    };
                    this.lineItemModel.operatingTo = updatedObj;
                }
            }
        }
        this.checkMilepostRanges();
        this.checkPrefixDifference();
    }

    private getBaseName(commonName: string): string {
        return commonName.split('[')[0].trim();
    }

    private buildFormattedName(baseName: string, track: AuthorityPoint): string {
        const milepostNum = parseFloat(track.location.milepost);
        let milepostStr: string;
        if (Number.isInteger(milepostNum)) {
            milepostStr = milepostNum.toString();
        } else {
            const truncated = Math.floor(milepostNum * 10) / 10;
            milepostStr = truncated.toFixed(1);
        }
        return `${baseName} [${track.location.prefix} ${milepostStr}]`;
    }

    private matchesCommonName(itemName: string, currentName: string, baseName: string): boolean {
        return itemName === currentName || itemName === baseName;
    }

    private matchesTracks(track1: AuthorityPoint, track2: AuthorityPoint): boolean {
        return track1.location.track.name === track2.location.track.name &&
            track1.location.prefix === track2.location.prefix &&
            track1.location.milepost === track2.location.milepost;
    }

    private updateOperatingLocationName(selector: string): void {
        const operatingSelector = 'operating' + selector;
        const selectedTrackSelector = 'selectedTrack' + selector;

        if (!this.lineItemModel[operatingSelector] || !this.lineItemModel[selectedTrackSelector]) {
            return;
        }

        const baseName = this.getBaseName(this.lineItemModel[operatingSelector].commonName);
        const idx = this.authorityPointGroupList.findIndex(
            item => this.matchesCommonName(item.commonName, this.lineItemModel[operatingSelector].commonName, baseName)
        );

        if (idx !== -1) {
            const updatedObj: AuthorityPointGroup = {
                ...this.authorityPointGroupList[idx],
                commonName: this.buildFormattedName(baseName, this.lineItemModel[selectedTrackSelector])
            };
            this.lineItemModel[operatingSelector] = updatedObj;
        }
    }

    checkPrefixDifference(): void {
        if (this.lineItemModel.selectedTrackFrom && this.lineItemModel.selectedTrackTo) {
            const fromPrefix = this.lineItemModel.selectedTrackFrom.location.prefix;
            const toPrefix = this.lineItemModel.selectedTrackTo.location.prefix;
            this.displayPrefixWarning = this.chosenPrefix !== fromPrefix || this.chosenPrefix !== toPrefix;
        } else {
            this.displayPrefixWarning = false;
        }
        // Submit should only be blocked for same-prefix milepost violations.
        this.outOfRangeViolation.emit(this.displayWarning);
    }

    checkMilepostRanges(): void {
        this.displayWarning = false;
        if (this.lineItemModel.selectedTrackFrom && this.lineItemModel.selectedTrackTo) {
            const fromPrefix = this.lineItemModel.selectedTrackFrom.location.prefix;
            const toPrefix = this.lineItemModel.selectedTrackTo.location.prefix;
            if (this.chosenPrefix !== fromPrefix || this.chosenPrefix !== toPrefix) {
                return;
            }
            const fromMilepost = Math.floor(parseFloat(this.lineItemModel.selectedTrackFrom.location.milepost) * 10) / 10;
            const toMilepost = Math.floor(parseFloat(this.lineItemModel.selectedTrackTo.location.milepost) * 10) / 10;
            const chosenMilepost = parseFloat(this.chosenMilepost);
            const minMilepost = Math.min(fromMilepost, toMilepost);
            const maxMilepost = Math.max(fromMilepost, toMilepost);
            if (chosenMilepost < minMilepost || chosenMilepost > maxMilepost) {
                this.displayWarning = true;
            }
        }
    }
}